import api from '../../api'

export const getEvents = async (data: {
  page: number
  page_size?: number
  search: string
  count?: string
  coach?: string[]
  venues: string[]
}) => {
  const res = await api.get('events/', {
    params: {
      ...data,
      coach: data.coach ? data.coach.map((c: any) => c.id).join(',') : null,
      venues: data.venues ? data.venues.map((c: any) => c.id).join(',') : null,
      hosted: true,
    },
  })
  return res.data
}

export const getEventDetail = async (id: string) => {
  const res = await api.get(`events/${id}/`)
  return res.data
}

export const postEvent = async (data: {
  title: string
  coaches: string[]
  type: string
  secondary_type?: string
  skill_level: number[]
  surface: number
  venue: string
  // New venue-wall-clock contract; the backend combines these in venue.timezone.
  // `start_time` is kept as a legacy fallback for callers that haven't migrated.
  start_date?: string
  start_time_local?: string
  start_time?: string
  // Phase 2 (Slice D): FK to Court (UUID). The structural court reference
  // enforced by the backend (Phase 1 plan 01-06).
  court_fk?: string | null
  // Phase 2 (Slice D): EVT-04 inline "Special event" Switch. Defaults to false on create.
  is_special_event?: boolean
  duration: number
  venue_fixed_rate: string
  venue_hourly_rate: string
  max_players: number
  description: string
  private_code: string | null
  fee: string
}) => {
  const res = await api.post('events/', data)
  return res.data
}

export const putEvent = async ({
  id,
  data,
}: {
  id: string | undefined
  data: {
    title?: string
    coaches?: string[]
    type?: string
    secondary_type?: string
    skill_level?: number[]
    surface?: number
    venue?: string
    start_time?: string
    court?: string
    // Phase 2 (Slice D): EVT-02 court selection by FK; backend protects against bad UUIDs.
    court_fk?: string | null
    // Phase 2 (Slice D): EVT-04 inline "Special event" Switch round-trips on edit (D-07).
    is_special_event?: boolean
    duration?: number
    fee?: string
    venue_cost?: string
    max_players?: number
    details?: string
    host?: string
    private_code?: string | null
  }
}) => {
  const res = await api.put(`events/${id}/`, data)
  return res.data
}

export const putBulkEditEvents = async ({
  event_ids,
  data,
  allow_partial = false,
}: {
  event_ids: string[]
  data: {
    title?: string
    description?: string
    type?: string
    secondary_type?: string
    skill_level?: number[]
    surface?: number | null
    court?: string | null
    start_time?: string
    duration?: number
    fee?: string
    venue_fixed_rate?: string
    venue_hourly_rate?: string
    max_players?: number | null
    private_code?: string | null
  }
  allow_partial?: boolean
}) => {
  const res = await api.put('events/bulk-edit/', {
    event_ids,
    data,
    allow_partial,
  })
  return res.data
}

export const deleteEvent = async ({
  id,
  data,
}: {
  id: string
  data: {
    details: string
    reason: string
  }
}) => {
  const res = await api.delete(`events/${id}/`, { data: data })
  return res.data
}

export const postVenue = async (data: {
  name: string
  address: string
  about: string
  opening_time: string
  closing_time: string

  website: string | undefined
  instagram: string | undefined
  video: string
  lat: number
  lng: number
  embed_code: string
  parking?: boolean
  parking_description?: string
}) => {
  const res = await api.post('venues/', data)
  return res.data
}

export const putParkingPass = async ({
  id,
  data,
}: {
  id: string
  data: {
    parking_pass: File | null
    description: string
  }
}) => {
  const formData = new FormData()
  formData.append('parking_pass', data.parking_pass ?? '')
  formData.append('description', data.description)
  const res = await api.put(`venues/${id}/parking_pass/`, formData)
  return res.data
}

export const postVenueImages = async ({
  id,
  data,
}: {
  id: string
  data: {
    images: File[] | null
  }
}) => {
  const formData = new FormData()
  if (data.images) data.images.forEach((image: any) => formData.append('images', image))
  const res = await api.post(`venues/${id}/images/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

export const putVenueImages = async ({
  id,
  data,
}: {
  id: string
  data: {
    images: File[] | null
  }
}) => {
  const formData = new FormData()
  if (data.images) data.images.forEach((image: any) => formData.append('images', image))
  const res = await api.put(`venues/${id}/images/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

export const putVenue = async ({
  id,
  data,
}: {
  id: string
  data: {
    name?: string
    address?: string
    about?: string
    opening_time?: string
    closing_time?: string
    website?: string
    instagram?: string
    video?: string
    lat?: number
    lng?: number
    embed_code?: string
    parking?: boolean
    parking_description?: string
  }
}) => {
  const res = await api.put(`venues/${id}/`, data)
  return res.data
}

export const putEventImage = async ({
  id,
  data,
}: {
  id: string
  data: {
    image: File | null
  }
}) => {
  const formData = new FormData()
  if (data.image) formData.append('file', data.image)
  const res = await api.put(`events/${id}/image/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return res.data
}

export const getRecurringEvents = async (params?: {
  future_only?: boolean
  host_only?: boolean
  include_past?: boolean
  summary_only?: boolean
  search?: string
  venue?: string
  recurrence_type?: string
  host_ids?: string
  page?: number
  page_size?: number
}) => {
  const res = await api.get('events/recurring/', {
    params: {
      future_only: params?.future_only ?? false,
      host_only: params?.host_only ?? true,
      include_past: params?.include_past ?? true,
      summary_only: params?.summary_only ?? false,
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.venue ? { venue: params.venue } : {}),
      ...(params?.recurrence_type ? { recurrence_type: params.recurrence_type } : {}),
      ...(params?.host_ids ? { host_ids: params.host_ids } : {}),
      ...(params?.page ? { page: params.page } : {}),
      ...(params?.page_size ? { page_size: params.page_size } : {}),
    },
  })
  return res.data
}

export const bulkEditRecurringEvents = async ({
  recurrence_series_id,
  parent_event_id,
  future_only,
  data,
}: {
  recurrence_series_id?: string
  parent_event_id?: string
  future_only?: boolean
  data: {
    title?: string
    description?: string
    additional_info?: string
    fee?: number
    venue_fixed_rate?: number
    venue_hourly_rate?: number
    max_players?: number
    duration?: number
    start_time?: string
    end_time?: string
    skill_level?: number[]
    type?: string
    secondary_type?: string
    surface?: number
    court_label?: string
    is_private?: boolean
    access_code?: string | null
    private_code?: string | null
  }
}) => {
  const params: any = {}
  if (recurrence_series_id) params.recurrence_series_id = recurrence_series_id
  if (parent_event_id) params.parent_event_id = parent_event_id
  if (future_only !== undefined) params.future_only = future_only

  const res = await api.put('events/bulk-edit-recurring/', data, {
    params,
  })
  return res.data
}

export const bulkDeleteRecurringEvents = async ({
  recurrence_series_id,
  parent_event_id,
  future_only,
  past_only,
}: {
  recurrence_series_id?: string
  parent_event_id?: string
  future_only?: boolean
  past_only?: boolean
}) => {
  const params: any = {}
  if (recurrence_series_id) params.recurrence_series_id = recurrence_series_id
  if (parent_event_id) params.parent_event_id = parent_event_id
  if (future_only !== undefined) params.future_only = future_only
  if (past_only !== undefined) params.past_only = past_only

  const res = await api.delete('events/bulk-delete-recurring/', {
    params,
  })
  return res.data
}
